import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/lib/apiClient';
import { useCurrentCabinet } from '@/hooks/cabinet/useCabinetCore';
import { trierCandidatsParDate } from './trierCandidatsParDate';

export interface OwnPriorPleadingCandidate {
  id: string;
  name: string;
  /** Backend marker used to hide generated dossier syntheses from source pickers. */
  document_type?: string | null;
  /** Date intrinsèque de la pièce (colonne SQL `date`). Peuplée à ~50 % sur cette catégorie. */
  document_date?: string | null;
}

/** The source kind is explicit because `procedure` is shared by unrelated document pickers. */
export function ownPriorPleadingsQueryKey(cabinetId: string | undefined, caseId: string | undefined) {
  return ['case-documents', cabinetId, caseId, 'own_prior_pleading', 'procedure', false] as const;
}

/**
 * Loads "Mes écritures" without silently narrowing the server-owned procedure scope.
 *
 * The list is sorted by intrinsic document date (descending, nulls last) so the most
 * recent prior act appears first — an advocate working on a reply picks their latest
 * conclusions, not the oldest.
 */
export function useOwnPriorPleadingsCandidates(caseId: string | undefined, enabled: boolean) {
  const currentCabinet = useCurrentCabinet();
  const cabinetId = currentCabinet.data?.id;

  return useQuery({
    queryKey: ownPriorPleadingsQueryKey(cabinetId, caseId),
    queryFn: async () => {
      if (!caseId || !cabinetId) {
        throw new Error(
          `Cannot load own prior pleadings: missing server scope cabinetId=${cabinetId ?? '<missing>'}, caseId=${caseId ?? '<missing>'}.`,
        );
      }
      const candidates = await apiClient.get<OwnPriorPleadingCandidate[]>(
        `/cases/${caseId}/documents?category=procedure&is_folder=false`,
      );
      // `procedure` also contains generated syntheses. They are not pleadings
      // the lawyer can select as the prior act, so exclude them before the UI
      // receives the candidate list.
      return candidates.filter((candidate) => candidate.document_type !== 'synthesis');
    },
    // `select` runs on every cache read, including pre-seeded Storybook fixtures —
    // keeps the sort consistent whether the data comes from the network or a test.
    select: (data) => trierCandidatsParDate(data),
    enabled: enabled && !!caseId && !!cabinetId,
    staleTime: 30_000,
  });
}
